import {
  pick,
  types,
  isErrorWithCode,
  errorCodes,
} from '@react-native-documents/picker';
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Alert,
  Image,
  TextInput,
  ActionSheetIOS,
} from 'react-native';

import {
  Play,
  Archive,
  Upload,
  ImageIcon,
  FileText,
  VideoIcon,
  FileIcon,
} from './../icons';
import { useRecentFilePickGuard } from '../../hooks/useRecentFilePickGuard';
import { useResponsive } from '../../hooks/useResponsive';
import { cameraService } from '../../services/cameraService';
import { dateFormatting } from '../../utils/dateFormatting';
import { validateFileForSecurity } from '../../utils/fileSecurityValidation';
import { showToast } from '../../utils/toast';
import { ACCESSORY_VIEW_ID } from '../shared/KeyboardDismissToolbar';

import type { DocumentPickerResponse } from '@react-native-documents/picker';

// Note: Native video thumbnail generation is implemented with placeholder
// For actual video thumbnails on native, consider libraries like:
// - react-native-video-thumbnail (with proper native linking)
// - expo-av for Expo projects
// - react-native-video with thumbnail extraction

export interface SelectedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File | DocumentPickerResponse;
  thumbnail?: string; // Base64 thumbnail data for videos or images
  thumbnailMimeType?: string; // MIME type of thumbnail (usually image/jpeg)
  imageThumbnailUrl?: string; // Object URL for image thumbnails
  caption?: string; // Caption for the file
  createdAt?: string; // Creation timestamp
}

interface FileUploadProps {
  label: string;
  requestSection: string; // Used for organizing files by form section
  onFilesSelected: (files: SelectedFile[]) => void;
  placeholder?: string;
  helperText?: string;
  accept?: 'images' | 'documents' | 'videos' | 'all';
  maxSizeInMB?: number; // Note: Base64 encoding adds ~33% overhead (150MB default = ~113MB raw file capacity)
  multiple?: boolean;
  disabled?: boolean;
  selectedFiles?: SelectedFile[];
  offlineMessage?: string; // Message to display when disabled due to offline mode
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  requestSection: _requestSection,
  onFilesSelected,
  placeholder = 'Click to upload files',
  helperText,
  accept = 'all',
  maxSizeInMB = 150, // 150MB limit per file (accounts for Base64 encoding overhead: ~113MB raw file capacity)
  multiple = false,
  disabled = false,
  selectedFiles: externalSelectedFiles,
  offlineMessage,
}) => {
  const isWeb = Platform.OS === 'web';
  const { isSmallScreen } = useResponsive();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const dropAreaRef = useRef<any>(null);

  // Get appropriate placeholder text based on screen size
  const getPlaceholderText = () => {
    if (isSmallScreen) {
      return multiple ? 'Add files' : 'Add file';
    }
    return placeholder;
  };

  // Helper function to generate thumbnail from video file (Web)
  const generateWebVideoThumbnail = (
    file: File,
  ): Promise<{ thumbnail: string; mimeType: string } | null> => {
    return new Promise(resolve => {
      try {
        if (typeof document === 'undefined') {
          resolve(null);
          return;
        }
        const video = document.createElement('video');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve(null);
          return;
        }

        video.onloadedmetadata = () => {
          // Set canvas dimensions (max 200x200 for thumbnail)
          const maxSize = 200;
          const aspectRatio = video.videoWidth / video.videoHeight;

          if (aspectRatio > 1) {
            canvas.width = maxSize;
            canvas.height = maxSize / aspectRatio;
          } else {
            canvas.width = maxSize * aspectRatio;
            canvas.height = maxSize;
          }

          video.currentTime = 0.1; // Slightly after start to avoid black frames
        };

        video.onseeked = () => {
          try {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
            const base64Data = thumbnailDataUrl.split(',')[1];

            resolve({
              thumbnail: base64Data,
              mimeType: 'image/jpeg',
            });
          } catch (error) {
            console.warn('Failed to generate video thumbnail:', error);
            resolve(null);
          }
        };

        video.onerror = () => {
          console.warn('Video loading failed for thumbnail generation');
          resolve(null);
        };

        video.src = (window as any).URL.createObjectURL(file);
        video.muted = true;
        video.preload = 'metadata';
      } catch (error) {
        console.warn('Thumbnail generation error:', error);
        resolve(null);
      }
    });
  };

  // Helper function to generate thumbnail from video file (React Native)
  const generateNativeVideoThumbnail = async (
    _file: DocumentPickerResponse,
  ): Promise<{ thumbnail: string; mimeType: string } | null> => {
    try {
      // For now, we'll create a simple placeholder for video files on native platforms
      // This can be enhanced later with actual video thumbnail generation

      // Create a simple video placeholder thumbnail (a grey square with play icon)
      // This is a base64 encoded 50x50 grey square with transparency
      const placeholderThumbnail =
        'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';

      console.log(
        'Using placeholder thumbnail for video file on native platform',
      );
      return {
        thumbnail: placeholderThumbnail,
        mimeType: 'image/png',
      };
    } catch (error) {
      console.warn('Native thumbnail generation error:', error);
      return null;
    }
  };

  // Helper function to check if file is a video
  const isVideoFile = (mimeType: string, fileName?: string): boolean => {
    // Check by MIME type first
    if (mimeType && mimeType.startsWith('video/')) {
      return true;
    }
    // Fallback: check by file extension
    if (fileName) {
      const ext = fileName.toLowerCase().split('.').pop() || '';
      const videoExtensions = [
        'mp4',
        'avi',
        'mov',
        'wmv',
        'flv',
        'webm',
        'mkv',
        'm4v',
        'mpeg',
        'mpg',
      ];
      return videoExtensions.includes(ext);
    }
    return false;
  };

  // Helper function to check if file is an image
  const isImageFile = (mimeType: string, fileName?: string): boolean => {
    // Check by MIME type first
    if (mimeType && mimeType.startsWith('image/')) {
      return true;
    }
    // Fallback: check by file extension
    if (fileName) {
      const ext = fileName.toLowerCase().split('.').pop() || '';
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
      return imageExtensions.includes(ext);
    }
    return false;
  };

  // Use internal state as fallback when no external state is provided
  const [internalSelectedFiles, setInternalSelectedFiles] = useState<
    SelectedFile[]
  >([]);

  // Use external state only if both external files AND external handler are provided
  // Otherwise fall back to internal state
  const hasExternalState =
    externalSelectedFiles !== undefined && onFilesSelected !== undefined;
  const selectedFiles = hasExternalState
    ? externalSelectedFiles || []
    : internalSelectedFiles;
  const setSelectedFiles = hasExternalState
    ? onFilesSelected
    : setInternalSelectedFiles;
  const guardRecentFilePicks = useRecentFilePickGuard();

  // Cleanup object URLs when component unmounts
  const selectedFilesRef = useRef(selectedFiles);
  selectedFilesRef.current = selectedFiles;
  useEffect(() => {
    return () => {
      if (isWeb) {
        selectedFilesRef.current.forEach(file => {
          if (file.imageThumbnailUrl) {
            try {
              (window as any).URL.revokeObjectURL(file.imageThumbnailUrl);
            } catch (error) {
              console.warn('Failed to revoke object URL on unmount:', error);
            }
          }
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAcceptedFileTypes = () => {
    // For specific media types, provide hints to file picker
    // For 'all', accept any file type (validation happens server-side)
    switch (accept) {
      case 'images':
        return 'image/*';
      case 'documents':
        return 'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation';
      case 'videos':
        return 'video/*';
      case 'all':
      default:
        return '*/*'; // Accept all file types
    }
  };

  const isValidFileType = (file: File): { valid: boolean; error?: string } => {
    console.log('File type validation:', {
      fileName: file.name,
      mimeType: file.type,
      acceptProp: accept,
    });

    // For specific accept types, use MIME type prefix matching (safer than includes)
    if (accept === 'images' && !file.type.startsWith('image/')) {
      return { valid: false, error: 'Only image files are allowed.' };
    }
    if (accept === 'videos' && !file.type.startsWith('video/')) {
      return { valid: false, error: 'Only video files are allowed.' };
    }
    if (accept === 'documents') {
      // Use startsWith for safer prefix matching
      const docMimePrefixes = [
        'application/pdf',
        'application/msword', // .doc
        'application/vnd.openxmlformats-officedocument', // .docx, .xlsx, .pptx
        'application/vnd.ms-excel', // .xls
        'application/vnd.ms-powerpoint', // .ppt
        'application/vnd.oasis.opendocument', // .odt, .ods
      ];
      if (!docMimePrefixes.some(mime => file.type.startsWith(mime))) {
        return {
          valid: false,
          error:
            'Only document files (PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX) are allowed.',
        };
      }
    }

    // For 'all' type, block executables via shared security utility (privacy-safe logging)
    if (accept === 'all') {
      const securityResult = validateFileForSecurity(file);
      if (!securityResult.valid) {
        return securityResult;
      }
    }

    return { valid: true };
  };

  const validateAndProcessFile = async (
    file: File,
  ): Promise<SelectedFile | null> => {
    const fileSizeMB = Math.round(file.size / 1024 / 1024);
    console.log('🔍 Starting file validation for:', file.name);
    console.log('   File size:', file.size, 'bytes (', fileSizeMB, 'MB)');
    console.log(
      '   Max size allowed:',
      maxSizeInMB,
      'MB (',
      maxSizeInMB * 1024 * 1024,
      'bytes)',
    );

    // Show processing indicator for large files (>10MB)
    if (fileSizeMB > 10) {
      setIsProcessing(true);
    }

    // Check file type
    const typeCheck = isValidFileType(file);
    console.log('   File type valid?', typeCheck.valid);
    if (!typeCheck.valid) {
      setIsProcessing(false);
      const typeMessage = typeCheck.error || 'This file type is not allowed.';
      console.error('File type validation failed:', typeMessage);
      showToast.error(typeMessage);
      return null;
    }

    // Check file size
    const sizeLimit = maxSizeInMB * 1024 * 1024;
    console.log(
      '   Size check: file.size (',
      file.size,
      ') > limit (',
      sizeLimit,
      ')?',
      file.size > sizeLimit,
    );
    if (file.size > sizeLimit) {
      setIsProcessing(false);
      console.error(
        '❌ File too large:',
        file.size,
        'bytes, limit:',
        sizeLimit,
        'bytes',
      );
      showToast.error(
        `File "${file.name}" is too large. Maximum size is ${maxSizeInMB}MB.`,
      );
      return null;
    }

    console.log('✅ File passed validation checks');

    const selectedFile: SelectedFile = {
      id: `${file.name}_${Date.now()}_${Math.random()}`,
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      createdAt: new Date().toISOString(),
    };

    // Generate thumbnail for video files (skip for large files >50MB to avoid memory issues)
    if (isVideoFile(file.type, file.name)) {
      console.log('📹 Detected video file:', file.name);
      if (fileSizeMB <= 50) {
        console.log('Generating thumbnail for video (size OK)...');
        try {
          const thumbnailResult = await generateWebVideoThumbnail(file);
          if (thumbnailResult) {
            selectedFile.thumbnail = thumbnailResult.thumbnail;
            selectedFile.thumbnailMimeType = thumbnailResult.mimeType;
            console.log('✅ Video thumbnail generated successfully');
          }
        } catch (error) {
          console.warn('Failed to generate thumbnail for video:', error);
        }
      } else {
        console.log(
          '⚠️ Video file too large for thumbnail generation (>50MB), skipping thumbnail',
        );
      }
    }

    // Generate thumbnail URL for image files
    if (isImageFile(file.type, file.name)) {
      console.log('🖼️ Detected image file:', file.name);
      try {
        selectedFile.imageThumbnailUrl = (window as any).URL.createObjectURL(
          file,
        );
      } catch (error) {
        console.warn('Failed to create thumbnail URL for image:', error);
      }
    }

    // Clear processing state
    setIsProcessing(false);

    return selectedFile;
  };

  const handleWebFileUpload = () => {
    if (typeof document === 'undefined') return;
    console.log('📂 Opening file picker...');
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = getAcceptedFileTypes();
    input.multiple = multiple;
    console.log('📋 Accepted file types:', input.accept);
    input.onchange = async (e: any) => {
      const files = (e.target as any).files;
      console.log('📁 Files selected:', files?.length || 0);
      if (files && files.length > 0) {
        const validFiles: SelectedFile[] = [];

        if (multiple) {
          // Process multiple files
          for (const file of Array.from(files) as File[]) {
            console.log(
              'Processing file:',
              file.name,
              'Type:',
              file.type,
              'Size:',
              file.size,
            );
            const processedFile = await validateAndProcessFile(file);
            if (processedFile) {
              console.log('✅ File validated and processed:', file.name);
              validFiles.push(processedFile);
            } else {
              console.warn('❌ File validation failed:', file.name);
            }
          }
        } else {
          // Process single file
          const file = files[0];
          console.log(
            'Processing single file:',
            file.name,
            'Type:',
            file.type,
            'Size:',
            file.size,
          );
          const processedFile = await validateAndProcessFile(file);
          if (processedFile) {
            console.log('✅ File validated and processed:', file.name);
            validFiles.push(processedFile);
          } else {
            console.warn('❌ File validation failed:', file.name);
          }
        }

        console.log('Valid files count:', validFiles.length);
        if (validFiles.length > 0) {
          const newFiles = multiple
            ? [...selectedFiles, ...validFiles]
            : validFiles;
          console.log('Setting files:', newFiles.length, 'files');
          setSelectedFiles(newFiles);
        } else {
          console.warn('⚠️ No valid files to add');
        }
      }
    };
    input.click();
  };

  const getNativeFileTypes = () => {
    switch (accept) {
      case 'images':
        return [types.images];
      case 'documents':
        return [types.pdf, types.doc, types.docx];
      case 'videos':
        return [types.video];
      case 'all':
        // Use allFiles so the OS picker doesn't hide unregistered types
        // like .msg / .eml. Security filtering (blocked executables,
        // content checks) is enforced client- and server-side after selection.
        return [types.allFiles];
      default:
        return [types.allFiles];
    }
  };

  const validateNativeFile = async (
    file: DocumentPickerResponse,
  ): Promise<SelectedFile | null> => {
    // Check file size
    if (file.size && file.size > maxSizeInMB * 1024 * 1024) {
      showToast.error(
        `File "${file.name}" is too large. Maximum size is ${maxSizeInMB}MB.`,
      );
      return null;
    }

    const selectedFile: SelectedFile = {
      id: `${file.name}_${Date.now()}_${Math.random()}`,
      name: file.name || 'Unknown file',
      size: file.size || 0,
      type: file.type || 'application/octet-stream',
      file: file,
      createdAt: new Date().toISOString(),
    };

    // Generate thumbnail for video files (placeholder for React Native implementation)
    if (isVideoFile(selectedFile.type, selectedFile.name)) {
      try {
        const thumbnailResult = await generateNativeVideoThumbnail(file);
        if (thumbnailResult) {
          selectedFile.thumbnail = thumbnailResult.thumbnail;
          selectedFile.thumbnailMimeType = thumbnailResult.mimeType;
        }
      } catch (error) {
        console.warn('Failed to generate thumbnail for video:', error);
      }
    }

    // For image files on React Native, use the URI directly
    if (isImageFile(selectedFile.type, selectedFile.name) && file.uri) {
      selectedFile.imageThumbnailUrl = file.uri;
    }

    return selectedFile;
  };

  const handleNativeFileUpload = async () => {
    try {
      const results = await pick({
        type: getNativeFileTypes(),
        allowMultiSelection: multiple,
      });

      const validFiles: SelectedFile[] = [];

      if (multiple) {
        // Process multiple files
        for (const file of results) {
          const processedFile = await validateNativeFile(file);
          if (processedFile) {
            validFiles.push(processedFile);
          }
        }
      } else {
        // Process single file
        const file = results[0];
        const processedFile = await validateNativeFile(file);
        if (processedFile) {
          validFiles.push(processedFile);
        }
      }

      if (validFiles.length > 0) {
        const picks = guardRecentFilePicks(validFiles);
        if (picks.length === 0) {
          return;
        }
        const newFiles = multiple ? [...selectedFiles, ...picks] : picks;
        setSelectedFiles(newFiles);
      }
    } catch (err) {
      if (
        !(isErrorWithCode(err) && err.code === errorCodes.OPERATION_CANCELED)
      ) {
        showToast.error(
          'An error occurred while picking the file. Please try again.',
        );
        console.error('DocumentPicker Error:', err);
      }
    }
  };

  const handleFileUpload = () => {
    if (disabled) return;

    if (isWeb) {
      handleWebFileUpload();
    } else {
      handleNativeFileUpload();
    }
  };

  // Camera handlers for native
  const handleTakePhoto = async () => {
    if (disabled) return;

    const photo = await cameraService.takePhoto();
    if (photo) {
      const picks = guardRecentFilePicks([photo]);
      if (picks.length === 0) return;
      const newFiles = multiple ? [...selectedFiles, ...picks] : picks;
      setSelectedFiles(newFiles);
    }
  };

  const handleRecordVideo = async () => {
    if (disabled) return;

    const video = await cameraService.recordVideo();
    if (video) {
      const picks = guardRecentFilePicks([video]);
      if (picks.length === 0) return;
      const newFiles = multiple ? [...selectedFiles, ...picks] : picks;
      setSelectedFiles(newFiles);
    }
  };

  const handleChooseFromPhotos = async () => {
    if (disabled) return;

    // Determine media type based on accept prop
    let mediaType: 'photo' | 'video' | 'mixed' = 'mixed';
    if (accept === 'images') {
      mediaType = 'photo';
    } else if (accept === 'videos') {
      mediaType = 'video';
    }

    const photos = await cameraService.chooseFromLibrary(mediaType);
    if (photos.length > 0) {
      const picks = guardRecentFilePicks(photos);
      if (picks.length === 0) return;
      const newFiles = multiple ? [...selectedFiles, ...picks] : picks;
      setSelectedFiles(newFiles);
    }
  };

  // Show action sheet for media selection on native
  const showMediaOptions = () => {
    if (disabled) return;

    // Build options based on accept prop
    const options: string[] = [];
    const handlers: (() => void | Promise<void>)[] = [];

    // Add "Choose from Photos" option for images/videos
    if (accept === 'images' || accept === 'videos' || accept === 'all') {
      options.push('Choose from Photos');
      handlers.push(handleChooseFromPhotos);
    }

    // Add "Choose from Files" for all file types
    options.push('Choose from Files');
    handlers.push(handleNativeFileUpload);

    // Add camera options
    if (accept === 'images' || accept === 'all') {
      options.push('Take Photo');
      handlers.push(handleTakePhoto);
    }

    if (accept === 'videos' || accept === 'all') {
      options.push('Record Video');
      handlers.push(handleRecordVideo);
    }

    options.push('Cancel');

    if (Platform.OS === 'ios') {
      // Use iOS ActionSheet
      ActionSheetIOS.showActionSheetWithOptions(
        {
          options,
          cancelButtonIndex: options.length - 1,
          title: 'Add Media',
          message: 'Choose how you want to add media',
        },
        buttonIndex => {
          if (buttonIndex < handlers.length) {
            handlers[buttonIndex]();
          }
        },
      );
    } else {
      // Use Android Alert (similar to ActionSheet)
      const buttons = options.slice(0, -1).map((option, index) => ({
        text: option,
        onPress: () => handlers[index](),
      }));
      buttons.push({
        text: 'Cancel',
        onPress: () => {},
        style: 'cancel' as const,
      });

      Alert.alert('Add Media', 'Choose how you want to add media', buttons);
    }
  };

  // Drag and drop event handlers for web
  const handleDragOver = (e: any) => {
    if (!isWeb || disabled) return;
    console.log('Drag over event triggered');
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
    setIsDragOver(true);
  };

  const handleDragEnter = (e: any) => {
    if (!isWeb || disabled) return;
    console.log('Drag enter event triggered');
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: any) => {
    if (!isWeb || disabled) return;
    console.log('Drag leave event triggered');
    e.preventDefault();
    e.stopPropagation();

    // Check if we're leaving the drop area itself, not just a child element
    if (
      !dropAreaRef.current ||
      !dropAreaRef.current.contains(e.relatedTarget)
    ) {
      console.log('Setting drag over to false');
      setIsDragOver(false);
    }
  };

  const handleDrop = async (e: any) => {
    if (!isWeb || disabled) return;
    console.log('Drop event triggered', e.dataTransfer?.files);
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const validFiles: SelectedFile[] = [];

      if (multiple) {
        // Process multiple files
        for (const file of Array.from(files) as File[]) {
          const processedFile = await validateAndProcessFile(file);
          if (processedFile) {
            validFiles.push(processedFile);
          }
        }
      } else {
        // Process single file
        const file = files[0] as File;
        const processedFile = await validateAndProcessFile(file);
        if (processedFile) {
          validFiles.push(processedFile);
        }
      }

      if (validFiles.length > 0) {
        const newFiles = multiple
          ? [...selectedFiles, ...validFiles]
          : validFiles;
        setSelectedFiles(newFiles);
      }
    }
  };

  const removeFile = (fileId: string) => {
    // Find the file being removed and cleanup its thumbnail URL if it exists
    const fileToRemove = selectedFiles.find(file => file.id === fileId);
    if (fileToRemove?.imageThumbnailUrl && isWeb) {
      try {
        (window as any).URL.revokeObjectURL(fileToRemove.imageThumbnailUrl);
      } catch (error) {
        console.warn('Failed to revoke object URL:', error);
      }
    }

    const updatedFiles = selectedFiles.filter(file => file.id !== fileId);
    setSelectedFiles(updatedFiles);
  };

  // Handler for mobile delete button
  const handleDeleteFile = (idToRemove: string) => {
    removeFile(idToRemove);
  };

  // Handler for caption changes
  const handleCaptionChange = (fileId: string, caption: string) => {
    const updatedFiles = selectedFiles.map(file =>
      file.id === fileId ? { ...file, caption } : file,
    );
    setSelectedFiles(updatedFiles);
  };

  const updateFileCaption = (fileId: string, caption: string) => {
    const updatedFiles = selectedFiles.map(file =>
      file.id === fileId ? { ...file, caption } : file,
    );
    setSelectedFiles(updatedFiles);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{label}</Text>

      {/* Show processing indicator */}
      {isProcessing && (
        <View style={styles.processingContainer}>
          <Text style={styles.processingText}>
            Processing large file, please wait...
          </Text>
        </View>
      )}

      {/* Show offline message if disabled and offlineMessage provided */}
      {disabled && offlineMessage && (
        <View style={styles.offlineMessageContainer}>
          <Text style={styles.offlineMessageText}>{offlineMessage}</Text>
        </View>
      )}

      {isWeb ? (
        isSmallScreen ? (
          <button
            data-testid="attachment-add-btn"
            style={{
              ...styles.simpleUploadButton,
              ...(disabled && styles.disabled),
            }}
            onClick={disabled ? undefined : handleFileUpload}
            disabled={disabled}
          >
            <Upload size={16} color="#fff" />
            <span style={styles.simpleUploadButtonText as any}>
              {getPlaceholderText()}
            </span>
          </button>
        ) : (
          <div
            ref={dropAreaRef}
            data-testid="attachment-add-btn"
            style={{
              ...styles.uploadArea,
              ...(disabled && styles.disabled),
              ...(isDragOver && styles.dragOver),
              ...styles.webUploadArea,
            }}
            onClick={disabled ? undefined : handleFileUpload}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload
              size={32}
              color="#666"
              style={styles.webUploadIcon as any}
            />
            <div style={styles.webUploadText as any}>
              {isDragOver ? 'Drop files here' : placeholder}
            </div>
            {helperText && (
              <div style={styles.webHelperText as any}>{helperText}</div>
            )}
            {!helperText && (
              <div style={styles.webDragDropHint as any}>
                Drag and drop files here or click to browse
              </div>
            )}
          </div>
        )
      ) : (
        // Native - Single button with action sheet
        <TouchableOpacity
          style={[styles.addMediaButton, disabled && styles.disabled]}
          onPress={showMediaOptions}
          disabled={disabled}
        >
          <Upload size={20} color="#fff" />
          <Text style={styles.addMediaButtonText}>
            {isSmallScreen ? 'Add Media' : 'Add Media'}
          </Text>
        </TouchableOpacity>
      )}

      {/* Helper text for native */}
      {!isWeb && helperText && (
        <Text style={styles.helperText}>{helperText}</Text>
      )}

      {selectedFiles.length > 0 && (
        <View style={styles.selectedFilesContainer}>
          <Text style={styles.selectedFilesTitle}>
            Selected Files ({selectedFiles.length})
          </Text>

          {isSmallScreen ? (
            // Mobile Card Layout
            <View style={styles.mobileFilesContainer}>
              {selectedFiles.map(file => (
                <View key={file.id} style={styles.fileCard}>
                  <View style={styles.fileCardHeader}>
                    <View style={styles.fileInfo}>
                      {/* Thumbnail */}
                      <View style={styles.mobileThumbnailContainer}>
                        {file.imageThumbnailUrl ? (
                          <Image
                            source={{ uri: file.imageThumbnailUrl }}
                            style={styles.mobileThumbnail}
                            resizeMode="cover"
                          />
                        ) : file.thumbnail && file.thumbnailMimeType ? (
                          <Image
                            source={{
                              uri: `data:${file.thumbnailMimeType};base64,${file.thumbnail}`,
                            }}
                            style={styles.mobileThumbnail}
                            resizeMode="cover"
                          />
                        ) : (
                          <View
                            style={[
                              styles.mobileThumbnail,
                              styles.mobileThumbnailPlaceholder,
                            ]}
                          >
                            {getFileIcon(file.type)}
                          </View>
                        )}
                      </View>

                      {/* File Details */}
                      <View style={styles.fileDetails}>
                        <Text style={styles.fileName} numberOfLines={2}>
                          {file.name}
                        </Text>
                        <Text style={styles.fileSubInfo}>
                          {formatFileSize(file.size)} •{' '}
                          {getFileTypeDisplay(file.type)}
                        </Text>
                      </View>
                    </View>

                    {/* Delete Button */}
                    <TouchableOpacity
                      style={styles.mobileDeleteButton}
                      onPress={() => handleDeleteFile(file.id)}
                      accessibilityLabel={`Delete ${file.name}`}
                    >
                      <Archive size={18} color="#e74c3c" />
                    </TouchableOpacity>
                  </View>

                  {/* Caption Input */}
                  <View style={styles.mobileCaptionContainer}>
                    <TextInput
                      key={
                        Platform.OS === 'ios'
                          ? `caption-${disabled}`
                          : undefined
                      }
                      style={styles.mobileCaptionInput}
                      value={file.caption || ''}
                      onChangeText={text => handleCaptionChange(file.id, text)}
                      placeholder="Add caption..."
                      placeholderTextColor="#999"
                      multiline={true}
                      numberOfLines={2}
                      editable={!disabled}
                      inputAccessoryViewID={
                        Platform.OS === 'ios' && !disabled
                          ? ACCESSORY_VIEW_ID
                          : undefined
                      }
                    />
                  </View>
                </View>
              ))}
            </View>
          ) : (
            // Desktop Table Layout
            <>
              {/* Table Header */}
              <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderText, styles.thumbnailColumn]}>
                  Preview
                </Text>
                <Text style={[styles.tableHeaderText, styles.nameColumn]}>
                  File Name
                </Text>
                <Text style={[styles.tableHeaderText, styles.sizeColumn]}>
                  Size
                </Text>
                <Text style={[styles.tableHeaderText, styles.typeColumn]}>
                  Type
                </Text>
                <Text style={[styles.tableHeaderText, styles.createdAtColumn]}>
                  Created At
                </Text>
                <Text style={[styles.tableHeaderText, styles.captionColumn]}>
                  Caption
                </Text>
                <Text style={[styles.tableHeaderText, styles.actionColumn]}>
                  Action
                </Text>
              </View>

              {/* Table Rows */}
              {selectedFiles.map(file => (
                <View key={file.id} style={styles.tableRow}>
                  {/* Thumbnail/Preview Column */}
                  <View style={styles.thumbnailColumn}>
                    {file.imageThumbnailUrl ? (
                      <View style={styles.thumbnailContainer}>
                        <Image
                          source={{ uri: file.imageThumbnailUrl }}
                          style={styles.tableThumbnail}
                          resizeMode="cover"
                        />
                      </View>
                    ) : file.thumbnail && file.thumbnailMimeType ? (
                      <View style={styles.thumbnailContainer}>
                        <Image
                          source={{
                            uri: `data:${file.thumbnailMimeType};base64,${file.thumbnail}`,
                          }}
                          style={styles.tableThumbnail}
                          resizeMode="cover"
                        />
                        <View style={styles.videoIconContainer}>
                          <Play size={8} color="white" />
                        </View>
                      </View>
                    ) : (
                      <View style={styles.fileTypeIcon}>
                        {file.type.includes('image') ? (
                          <ImageIcon size={20} color="#28a745" />
                        ) : file.type.includes('video') ? (
                          <VideoIcon size={20} color="#6610f2" />
                        ) : file.type.includes('pdf') ? (
                          <FileText size={20} color="#dc3545" />
                        ) : (
                          <FileIcon size={20} color="#6c757d" />
                        )}
                      </View>
                    )}
                  </View>

                  {/* File Name Column */}
                  <View style={styles.nameColumn}>
                    <Text style={styles.tableFileName} numberOfLines={2}>
                      {file.name}
                    </Text>
                  </View>

                  {/* Size Column */}
                  <View style={styles.sizeColumn}>
                    <Text style={styles.tableFileSize}>
                      {formatFileSize(file.size)}
                    </Text>
                  </View>

                  {/* Type Column */}
                  <View style={styles.typeColumn}>
                    <Text style={styles.tableFileType}>
                      {file.type.split('/')[0].toUpperCase()}
                    </Text>
                    {isVideoFile(file.type, file.name) && (
                      <Text style={styles.videoLabel}>VIDEO</Text>
                    )}
                  </View>

                  {/* Created At Column */}
                  <View style={styles.createdAtColumn}>
                    <Text style={styles.tableCreatedAt}>
                      {formatCreatedAt(file.createdAt)}
                    </Text>
                  </View>

                  {/* Caption Column */}
                  <View style={styles.captionColumn}>
                    <TextInput
                      key={
                        Platform.OS === 'ios'
                          ? `caption-${disabled}`
                          : undefined
                      }
                      style={styles.tableCaptionInput}
                      placeholder="Add caption..."
                      value={file.caption || ''}
                      onChangeText={text => updateFileCaption(file.id, text)}
                      multiline
                      numberOfLines={3}
                      editable={!disabled}
                      inputAccessoryViewID={
                        Platform.OS === 'ios' && !disabled
                          ? ACCESSORY_VIEW_ID
                          : undefined
                      }
                    />
                  </View>

                  {/* Action Column */}
                  <View style={styles.actionColumn}>
                    <TouchableOpacity
                      style={styles.tableRemoveButton}
                      onPress={() => removeFile(file.id)}
                    >
                      <Archive size={16} color="#dc3545" />
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </>
          )}
        </View>
      )}
    </View>
  );
};

// Helper function to get file icon based on type
const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) {
    return <ImageIcon size={16} color="#666" />;
  } else if (type.startsWith('video/')) {
    return <VideoIcon size={16} color="#666" />;
  } else {
    return <FileIcon size={16} color="#666" />;
  }
};

// Exported so other components can reuse it
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to format creation date
const formatCreatedAt = (createdAt?: string): string => {
  if (!createdAt) return 'Unknown';
  return (
    dateFormatting.format(createdAt, {
      locale: 'en-US',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }) ?? 'Invalid date'
  );
};

// Helper function to get readable file type
const getFileTypeDisplay = (type: string): string => {
  const parts = type.split('/');
  if (parts.length > 1) {
    return parts[1].toUpperCase();
  }
  return type.toUpperCase();
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 8,
    textAlign: 'center',
  },
  processingContainer: {
    backgroundColor: '#e3f2fd',
    borderWidth: 1,
    borderColor: '#2196f3',
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
  },
  processingText: {
    fontSize: 13,
    color: '#1565c0',
    textAlign: 'center',
    fontWeight: '500',
  },
  offlineMessageContainer: {
    backgroundColor: '#fff3cd',
    borderWidth: 1,
    borderColor: '#ffc107',
    borderRadius: 6,
    padding: 12,
    marginBottom: 12,
  },
  offlineMessageText: {
    fontSize: 13,
    color: '#856404',
    textAlign: 'center',
  },
  uploadArea: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 20,
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    ...(Platform.OS === 'web' && { transition: 'all 0.2s ease-in-out' }),
  } as any,
  dragOver: {
    borderColor: '#007bff',
    backgroundColor: '#e3f2fd',
    ...(Platform.OS === 'web' && { transform: [{ scale: 1.02 }] }),
  },
  disabled: {
    opacity: 0.5,
    backgroundColor: '#f5f5f5',
  },
  uploadText: {
    fontSize: 16,
    color: '#333',
    marginTop: 8,
  },
  helperText: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
    textAlign: 'center',
  },
  fileTypeHint: {
    fontSize: 12,
    color: '#888',
    marginTop: 4,
    textAlign: 'center',
  },
  sizeHint: {
    fontSize: 11,
    color: '#999',
    marginTop: 2,
    textAlign: 'center',
  },
  selectedFilesContainer: {
    marginTop: 16,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    overflow: 'hidden',
  },
  selectedFilesTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#495057',
    padding: 12,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },

  // Table Styles
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f1f3f4',
    paddingVertical: 8,
    paddingHorizontal: 4,
    borderBottomWidth: 2,
    borderBottomColor: '#e9ecef',
  },
  tableHeaderText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#495057',
    textAlign: 'left',
  },
  centerText: {
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 6,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f3f4',
    alignItems: 'center',
    minHeight: 50,
  },

  // Column Styles
  thumbnailColumn: {
    width: 60,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nameColumn: {
    flex: 2,
    paddingHorizontal: 8,
    justifyContent: 'center',
  },
  sizeColumn: {
    width: 80,
    alignItems: 'center',
  },
  typeColumn: {
    width: 80,
    alignItems: 'center',
  },
  createdAtColumn: {
    width: 140,
    minWidth: 140,
    alignItems: 'flex-start',
    paddingHorizontal: 12,
  },
  captionColumn: {
    flex: 2,
    paddingHorizontal: 8,
  },
  actionColumn: {
    width: 50,
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Table Content Styles
  tableThumbnail: {
    width: 40,
    height: 40,
    borderRadius: 4,
    backgroundColor: '#f0f0f0',
  },
  thumbnailContainer: {
    position: 'relative',
  },
  videoPlaceholder: {
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#e0e0e0',
  },
  videoIconContainer: {
    position: 'absolute',
    bottom: 1,
    right: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 6,
    paddingHorizontal: 2,
    paddingVertical: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileTypeIcon: {
    width: 40,
    height: 40,
    borderRadius: 4,
    backgroundColor: '#f8f9fa',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  tableFileName: {
    fontSize: 13,
    color: '#212529',
    fontWeight: '500',
    lineHeight: 16,
  },
  tableFileSize: {
    fontSize: 11,
    color: '#6c757d',
    textAlign: 'center',
  },
  tableFileType: {
    fontSize: 10,
    color: '#495057',
    fontWeight: '600',
    textAlign: 'center',
  },
  tableCreatedAt: {
    fontSize: 12,
    color: '#6c757d',
    fontWeight: '400',
    textAlign: 'left',
  },
  videoLabel: {
    fontSize: 8,
    color: '#007bff',
    fontWeight: '600',
    marginTop: 2,
  },
  tableCaptionInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    padding: 4,
    fontSize: 11,
    backgroundColor: '#f9f9f9',
    minHeight: 32,
    maxHeight: 45,
    textAlignVertical: 'top',
  },
  tableRemoveButton: {
    padding: 6,
    borderRadius: 4,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#dc3545',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dragDropHint: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
    textAlign: 'center',
    fontStyle: 'italic',
  },

  // Web-specific styles
  webUploadArea:
    Platform.OS === 'web'
      ? {
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }
      : {},
  webUploadIcon:
    Platform.OS === 'web'
      ? {
          marginBottom: 8,
        }
      : {},
  webUploadText:
    Platform.OS === 'web'
      ? {
          fontSize: 16,
          color: '#333',
          marginTop: 8,
          textAlign: 'center',
        }
      : {},
  webHelperText:
    Platform.OS === 'web'
      ? {
          fontSize: 12,
          color: '#666',
          marginTop: 4,
          textAlign: 'center',
        }
      : {},
  webDragDropHint:
    Platform.OS === 'web'
      ? {
          fontSize: 12,
          color: '#666',
          marginTop: 4,
          textAlign: 'center',
          fontStyle: 'italic',
        }
      : {},

  // Add Media button for native (shows action sheet)
  addMediaButton: {
    backgroundColor: '#007bff',
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 24,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    minWidth: 160,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  addMediaButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
  },

  // Simple button styles for small screens
  simpleUploadButton: {
    backgroundColor: '#6c757d',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    minWidth: 120,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      border: 'none',
      outlineStyle: 'none',
      outlineWidth: 0,
    }),
  },
  simpleUploadButtonText: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '500',
    marginLeft: 8,
    ...(Platform.OS === 'web' && {
      userSelect: 'none',
    }),
  },

  // Mobile-specific styles
  mobileFilesContainer: {
    marginTop: 16,
  },
  fileCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    marginBottom: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  fileCardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  fileInfo: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
  },
  mobileThumbnailContainer: {
    marginRight: 12,
  },
  mobileThumbnail: {
    width: 40,
    height: 40,
    borderRadius: 6,
  },
  mobileThumbnailPlaceholder: {
    backgroundColor: '#e9ecef',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fileDetails: {
    flex: 1,
  },
  fileName: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 4,
  },
  fileSubInfo: {
    fontSize: 12,
    color: '#666',
  },
  mobileDeleteButton: {
    padding: 8,
    marginLeft: 8,
  },
  mobileCaptionContainer: {
    marginTop: 8,
  },
  mobileCaptionInput: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    backgroundColor: '#fff',
    minHeight: 36,
  },
});

export { FileUpload };
